<template>
  <div class="filters-container d-flex w-100">
    <div class="d-flex px-0">
      <!-- <span class="mr-2 text-gray filter-title">Filtrer par : </span> -->

      <!-- Discipline Filter -->
      <v-autocomplete
        v-model="selectedDiscipline"
        :items="searchFiltersStore.disciplines"
        item-title="formattedTitle"
        item-value="value"
        hide-details
        density="compact"
        class="filter-item discipline"
        outlined
        :label="$t('disciplines.label')"
        placeholder="Discipline"
      />

      <!-- Consultation Type Filter -->
      <v-select
        v-model="consultationType"
        :items="[
          { title: $t('consultation.all'), value: 'all' },
          { title: $t('consultation.remote'), value: 'remote' },
          { title: $t('consultation.in_person'), value: 'in-person' },
        ]"
        :label="$t('consultation.type')"
        class="filter-item"
        outlined
        hide-details
      />

      <!-- Price Order Filter -->
      <v-select
        v-model="sortBy"
        :items="filteredSortByOptions"
        :label="$t('sortby.label')"
        class="filter-item"
        outlined
        hide-details
      />

      <!-- Opinion Order Filter -->
      <!-- <v-select
        v-model="opinionOrder"
        :items="[
          { title: $t('opinions.all'), value: 'all' },
          { title: $t('opinions.best_rated'), value: 'desc' },
          { title: $t('opinions.worst_rated'), value: 'asc' }
        ]"
        :label="$t('opinions.label')"
        class="filter-item"
        outlined
        hide-details
      /> -->
    </div>
    <span class="text-primary delete-filters-btn" @click="resetFilters()">
      {{ $t('searchPage.deleteFilter') }}
    </span>
    <div class="d-flex w-100 justify-start mr-5"></div>
  </div>
</template>
<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMainSearchBarStore } from '@/store/search-page/mainSearchBarStore'
import { useSearchFiltersStore } from '@/store/search-filters/searchFiltersStore'
import createHttpClient from '@/api/httpClient'

const $t = useI18n().t
const mainSearchBarStore = useMainSearchBarStore()
const searchFiltersStore = useSearchFiltersStore()
const client = createHttpClient()
const isInitializing = ref(true)

const disciplines = ref([])
const selectedDiscipline = ref('')
const priceOrder = ref<'asc' | 'desc' | 'all'>('all')
const sortBy = ref<
  | 'pertinence'
  | 'proximity'
  | 'price.asc'
  | 'price.desc'
  | 'opinions.asc'
  | 'opinions.desc'
>('proximity')
const consultationType = ref<'all' | 'remote' | 'in-person'>('all')
const opinionOrder = ref<'asc' | 'desc' | 'all'>('all')

// Fonction pour appliquer les filtres automatiquement
const applyFilters = () => {
  if (selectedDiscipline.value == mainSearchBarStore.searchText) {
    selectedDiscipline.value = mainSearchBarStore.searchText
  }
  if (selectedDiscipline.value == null) {
    selectedDiscipline.value = ''
  }
  mainSearchBarStore.selectedDiscipline = selectedDiscipline.value
  mainSearchBarStore.priceOrder = priceOrder.value
  mainSearchBarStore.sortBy = sortBy.value
  mainSearchBarStore.consultationType = consultationType.value
  mainSearchBarStore.opinionOrder = opinionOrder.value

  updateFilters()
  mainSearchBarStore.findYourPractitioner()
}

const resetFilters = () => {
  selectedDiscipline.value = ''
  priceOrder.value = 'all'
  sortBy.value = 'proximity'
  consultationType.value = 'all'
  opinionOrder.value = 'all'
  searchFiltersStore.resetFilters()
}

// Options initiales pour sortBy
const sortByOptions = [
  { title: $t('pertinence'), value: 'pertinence' },
  { title: $t('proximity'), value: 'proximity' },
  { title: $t('price.low_to_high'), value: 'price.asc' },
  { title: $t('price.high_to_low'), value: 'price.desc' },
  { title: $t('opinions.best_rated'), value: 'opinions.desc' },
]

// Options filtrées en fonction de consultationType
const filteredSortByOptions = computed(() => {
  // Retirer l'option "proximity" si consultationType est "remote"
  if (consultationType.value === 'remote') {
    return sortByOptions.filter(option => option.value !== 'proximity')
  }
  return sortByOptions
})

// Watchers pour déclencher l'application des filtres
watch(
  [selectedDiscipline, priceOrder, sortBy, consultationType, opinionOrder],
  () => {
    if (!mainSearchBarStore.isInitializing) {
      applyFilters()
    }
  },
)

watch(consultationType, newVal => {
  if (newVal === 'remote' && sortBy.value === 'proximity') {
    sortBy.value = 'pertinence'
  }
})

// Fetch des données pour les disciplines
const fetchSelectData = async () => {
  try {
    const response = await client.get('/discipline/list')
    disciplines.value = response.data
    disciplines.value.push({ id: '', slug: '', name: 'Toutes les disciplines' })
  } catch (error) {
    console.error('Error fetching data:', error)
  }
}

const updateFilters = () => {
  searchFiltersStore.selectedDiscipline = selectedDiscipline.value
  searchFiltersStore.priceOrder = priceOrder.value
  searchFiltersStore.consultationType = consultationType.value
  searchFiltersStore.opinionOrder = opinionOrder.value
  searchFiltersStore.sortBy = sortBy.value
}

onMounted(() => {
  fetchSelectData()

  if (searchFiltersStore.selectedDiscipline) {
    selectedDiscipline.value = searchFiltersStore.selectedDiscipline
  }

  if (searchFiltersStore.priceOrder) {
    priceOrder.value = searchFiltersStore.priceOrder
  }

  if (searchFiltersStore.sortBy) {
    sortBy.value = searchFiltersStore.sortBy
    mainSearchBarStore.sortBy = searchFiltersStore.sortBy
  }

  if (searchFiltersStore.consultationType) {
    consultationType.value = searchFiltersStore.consultationType
  }

  if (searchFiltersStore.opinionOrder) {
    opinionOrder.value = searchFiltersStore.opinionOrder
  }

  isInitializing.value = false
})
</script>
<style scoped>
.filters-container {
  padding: 16px;
  /* border: 1px solid #e0e0e0;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); */
  /* gap: 16px; */
  /* margin-bottom: 20px; */
  justify-content: space-between;
  flex-wrap: wrap;
}

.filter-item {
  max-width: 320px;
  min-width: 290px;
  margin-right: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.v-select,
.v-autocomplete {
  border-radius: 8px;
}

:deep(.v-select .v-field__input) {
  padding: 5px !important;
  padding-inline: 15px !important;
  min-height: 40px;
}

.v-btn {
  margin-left: auto;
  height: 40px;
  border-radius: 20px;
}

.d-flex {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.delete-filters-btn {
  cursor: pointer;
  margin-bottom: 15px;
  font-weight: 500;
  border: 1px solid #007058;
  border-radius: 20px;
  padding-inline: 10px;
  background-color: rgba(0, 112, 88, 0.034);
}

.filter-title {
  margin-bottom: 15px;
}

/* Styles spécifiques pour les mobiles */
@media (max-width: 768px) {
  .filter-item {
    max-width: unset;
    min-width: unset;
    margin-right: 0px;
    margin-bottom: 25px;
  }
}
</style>
